/**
 * Asserts that a given value is of type `never`, which is the type left after all other cases have been removed, see
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html.
 * @param x - The value that should never occur.
 */
export function assertNever(x: never): never {
  throw new Error('Unexpected object: ' + x);
}

/**
 * Returns the values in a given array that are not `null` (or `undefined`). Note that without the `strictNullChecks` compiler option, the
 * values in an array `xs` that are not `null` (or `undefined`) can be obtained from the expression `xs.filter(x => x)`.
 * @param xs - The array to obtain values from that are not `null`.
 */
export function removeNulls<T>(xs: (T | null | undefined)[]): T[] {
  let ys: T[] = [];
  xs.forEach(x => {
    if (x !== null && x !== undefined) {
      ys.push(x);
    }
  });
  return ys;
}

/**
 * Compares two arrays for shallow equality (IE: They contain the same scalar values)
 */
export function scalarArraysEq<T>(arr1: T[], arr2: T[]): boolean {
  if (arr1.length !== arr2.length) { return false; }
  for (let i = 0; i < arr1.length; ++i) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Maps a function across an array, flattening any sub-arrays generated by the function.
 */
export function flatMap<T, U>(array: T[], mapFunc: (x: T) => U[]): U[] {
  return array.reduce((cumulus: U[], next: T) => [...mapFunc(next), ...cumulus], <U[]> [])
    .reverse();
}
